import React, { useEffect, useState } from "react";
import { Typography, Stack, Box, Tooltip } from "@mui/material";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import ActionButton from "../widgets/ActionButton";
import DeleteIconButton from "../widgets/DeleteIconButton";
import {
  DELETE_QUERY,
  GET_DIAMS,
  GET_SAVED_QUERIES,
  tokenOptions,
} from "../../backend/graphql";
import { useMutation, useQuery } from "@apollo/client";
import { useConfirm } from "material-ui-confirm";
import { useAppSelector } from "../../app/hooks";
import { Link } from "react-router-dom";
import PageTitle from "../widgets/PageTitle";
import SectionTitle from "../widgets/SectionTitle";

const DashCard = ({
  item,
  edit,
  upperRefetch,
}: {
  item: any;
  edit: boolean;
  upperRefetch: any;
}) => {
  const role = useAppSelector((state) => state.auth.role);
  const token = useAppSelector((state) => state.auth.token);
  const { loading, data } = useQuery(GET_DIAMS, {
    ...tokenOptions(token),
    variables: {
      take: 1,
      skip: 0,
      filter: item.query,
    },
  });

  const [deleteQuery] = useMutation(DELETE_QUERY, tokenOptions(token));
  const confirm = useConfirm();

  const handleDelete = async (event: React.MouseEvent<HTMLButtonElement>) => {
    // The click must not follow the card's link, whether or not the delete goes ahead.
    event.preventDefault();
    try {
      await confirm({
        description: `Delete the saved query "${item.name}"?`,
        confirmationText: "Delete",
      });
    } catch {
      return; // dismissed: material-ui-confirm rejects rather than resolving false
    }
    await deleteQuery({ variables: { id: item.id } });
    upperRefetch();
  };

  return (
    <Link
      to={"/alerts/dataquality/" + item.id}
      target="_blank"
      rel="noopener"
      style={{ textDecoration: "none", display: "block", height: "100%" }}
    >
      {/* The card fills its cell, and every cell is the same height, so a two-line name does not
          leave the cards beside it short. The count stays at the top and the name below it, with
          the slack falling between them rather than stretching either. */}
      <Box
        sx={{
          position: "relative",
          height: "100%",
          border: 1,
          borderColor: "divider",
          borderRadius: 1,
          p: 2,
          textAlign: "center",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {item.adminOnly && (
          <Tooltip title="Admin Only">
            <LockOutlinedIcon
              sx={{
                position: "absolute",
                top: 8,
                right: 8,
                fontSize: "1rem",
                color: "text.secondary",
              }}
            />
          </Tooltip>
        )}
        <Typography sx={{ fontSize: "200%", fontWeight: 600 }}>
          {loading ? "…" : data?.diamonds?.totalItems}
        </Typography>
        <Typography color="text.secondary">{item.name}</Typography>
        {role === "Admin" && edit && (
          /* The card is a flex column, so the button has to align itself: stretched to the card's
             width a round IconButton comes out an ellipse. The auto top margin drops it to the
             bottom and leaves the slack above it. */
          <DeleteIconButton
            onClick={handleDelete}
            sx={{ mt: "auto", alignSelf: "center" }}
          />
        )}
      </Box>
    </Link>
  );
};

// The saved queries by section: those with none first, under no heading, then the sections
// alphabetically, and the queries alphabetically within each — so a tile keeps its place from one
// visit to the next (the list comes back in insertion order otherwise).
const sections = (items: any[]): [string, any[]][] => {
  const bySection = new Map<string, any[]>();
  for (const item of items) {
    const section = item.section ?? "";
    bySection.set(section, [...(bySection.get(section) ?? []), item]);
  }
  // The empty name sorts first on its own.
  return Array.from(bySection.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([section, queries]) => [
      section,
      queries.sort((a, b) => a.name.localeCompare(b.name)),
    ]);
};

export const DataQuality = () => {
  const token = useAppSelector((state) => state.auth.token);
  const { loading, data, refetch } = useQuery(GET_SAVED_QUERIES, {
    ...tokenOptions(token),
    variables: {
      take: 100,
      skip: 0,
      filter: 'category == "QADashboard"',
    },
  });
  const [edit, setEdit] = useState(false);
  const role = useAppSelector((state) => state.auth.role);

  // Switching role (RoleSwitcher) mints a new token but doesn't change this query's variables, so
  // Apollo won't refetch on its own — the Admin-only filtering only takes effect server-side once
  // this is reissued with the new token.
  useEffect(() => {
    refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role]);

  return (
    <Stack spacing={1} useFlexGap>
      <PageTitle
        actions={
          role === "Admin" && (
            <ActionButton onClick={() => setEdit(!edit)}>
              {edit ? "Done" : "Edit"}
            </ActionButton>
          )
        }
      />
      {loading ? (
        <p>Loading...</p>
      ) : (
        sections(data?.savedQueries?.items ?? []).map(([section, items]) => (
          <Box key={section}>
            {section && <SectionTitle>{section}</SectionTitle>}
            {/* A css grid rather than rows of columns: `1fr` auto-rows makes every row of a section
                as tall as its tallest card, so the cards read as one set instead of each row
                finding its own height. */}
            <Box
              sx={{
                display: "grid",
                gap: 1,
                gridTemplateColumns: {
                  xs: "repeat(2, 1fr)",
                  sm: "repeat(4, 1fr)",
                  xl: "repeat(12, 1fr)",
                },
                gridAutoRows: "1fr",
              }}
            >
              {items.map((item: any) => (
                <DashCard
                  key={item.id}
                  item={item}
                  edit={edit}
                  upperRefetch={refetch}
                />
              ))}
            </Box>
          </Box>
        ))
      )}
    </Stack>
  );
};
